// Shared project record drawer (round 4). One overlay host for the current
// project record, opened from Portfolio rows and the Updates "Record" button.
// Reuses ProjectFullPage recordOnly (no rebuilt sections) inside SlideSheet.
//
// Guard contract (owner-facing):
//   - Editor state comes from ProjectFullPage.onEditorStateChange
//     ({ editing, busy }) aggregated over the edit modal, schedule and every
//     record section - no DOM heuristics.
//   - requestClose(): closes when clean; when editing, shows a discard
//     confirm; when busy (save in flight), never closes - shows a wait status
//     and Discard is disabled. Escape/scrim are ignored while a stacked modal
//     (record edit dialog, z-index 10000 above the sheet's 1101) is open.
//   - guardRef.current() -> { dirty, busy }. Owners must not switch the
//     drawer's project or unmount it while dirty; never while busy.
//   - In-app navigation (anchor clicks, back/forward) is blocked through
//     recordNavigationGuard while dirty; a busy drawer refuses to proceed.
//   - The SlideSheet panel itself is the dialog (role/aria-modal/name), so
//     the Close button is inside the named dialog. Tab/Shift+Tab are
//     contained in the panel; focus returns to the opener on close.
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { UNSAFE_LocationContext, UNSAFE_NavigationContext } from "react-router-dom";
import { T, LAYERS } from "../../app/tokens.js";
import { SlideSheet } from "../../components/SlideSheet.jsx";
import { PrevNextPager } from "../../components/PrevNextPager.jsx";
import { ProjectFullPage } from "./ProjectFullPage.jsx";
import { ParticipationStrip } from "./weekly/WeeklyProjectCard.jsx";
import { guardRouterNavigator, registerNavigationGuard, syncNavigationIndex } from "./recordNavigationGuard.js";

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
const PANEL_SEL = "[data-testid='project-record-drawer']";
const IDLE = Object.freeze({ editing: false, busy: false });
const btn = { border: `1px solid ${T.border}`, background: T.card, borderRadius: 8, padding: "5px 11px", fontSize: T.fsXs, fontWeight: T.fwBold, color: T.dark, cursor: "pointer" };

/** Pure: provenance copy for the record shown in the drawer. */
export function recordProvenance({ frozen = false, readOnly = false, origin = "portfolio" } = {}) {
  if (frozen) return "Current project record - today's metadata, not part of this week's frozen archive. Read-only here.";
  if (readOnly && origin === "updates") return "Current project record - today's metadata, read-only outside the live week.";
  return "Current project record - PMO-owned, separate from weekly updates.";
}

/** Pure: what a close/switch/navigation request may do given editor state. */
export function drawerCloseDecision({ editing = false, busy = false } = {}) {
  if (busy) return "wait";
  if (editing) return "confirm";
  return "close";
}

/** True when another modal dialog (not the drawer panel) is open above it. */
export function stackedDialogOpen(panel) {
  return [...document.querySelectorAll('[role="dialog"][aria-modal="true"], [role="alertdialog"]')]
    .some((d) => d !== panel && !(panel && panel.contains(d)) && !d.contains(panel));
}

function Participation({ participation, frozen }) {
  const st = participation || { status: "unavailable" };
  if (st.status === "loading") return <div data-testid="record-drawer-participation-loading" aria-busy="true" style={{ height: 30, margin: "12px 0", borderRadius: 8, background: T.lightGray }} />;
  if (st.status === "error") {
    return (
      <div role="alert" data-testid="record-drawer-participation-error" style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 0", fontSize: T.fsSm, color: T.mid }}>
        <span style={{ flex: 1 }}>Ownership and tracks are unavailable: {st.error || "the current-week read failed."}</span>
        {st.onRetry && <button type="button" data-testid="record-drawer-participation-retry" onClick={st.onRetry} style={btn}>Retry</button>}
      </div>
    );
  }
  if (st.status === "ok" && st.part) {
    return (
      <>
        {frozen && <div style={{ fontSize: T.fsXs, color: T.mid, paddingTop: 10 }}>Selected-week participation · captured archive</div>}
        <ParticipationStrip part={st.part} />
      </>
    );
  }
  return (
    <div data-testid="record-drawer-participation-unavailable" style={{ fontSize: T.fsSm, color: T.mid, fontStyle: "italic", padding: "12px 0" }}>
      {st.reason || "Ownership and track participation is unavailable for this project."}
    </div>
  );
}

export function ProjectRecordDrawer({
  projectId, projectName = "", open, onClose, currentRole = "viewer", readOnly = false, frozen = false,
  origin = "portfolio", participation = null, onRecordSaved, onArchived, guardRef, orderedProjectIds = [], onOpenProject, navigationDisabled = false,
}) {
  // Standalone readers are also used in cleanup-free component tests. The
  // routed application remains the sole URL owner; no manual location store.
  const location = useContext(UNSAFE_LocationContext)?.location;
  const navigator = useContext(UNSAFE_NavigationContext)?.navigator;
  const bodyRef = useRef(null);
  const returnFocus = useRef(null);
  const editor = useRef(IDLE);
  const [editorState, setEditorState] = useState(IDLE);
  const [ask, setAsk] = useState(null); // null | { proceed?: fn, label }
  const pending = useRef(null);
  const navigation = useRef(null);
  navigation.current = { active: open && Boolean(projectId), projectId, ids: orderedProjectIds, onOpenProject, disabled: navigationDisabled };
  const askOnce = (intent) => {
    if (pending.current) return;
    pending.current = intent;
    setAsk(intent);
  };
  const clearAsk = () => { pending.current = null; setAsk(null); };
  const [width, setWidth] = useState(() => (typeof window === "undefined" ? 640 : Math.min(680, window.innerWidth)));
  const active = open && Boolean(projectId);
  // Each open resets the record tabs to Overview (project changes remount).
  const [openToken, setOpenToken] = useState(0);
  useEffect(() => { if (active) setOpenToken((n) => n + 1); }, [active]);
  useEffect(() => active ? guardRouterNavigator(navigator) : undefined, [active, navigator]);

  const onEditorStateChange = useCallback((st) => {
    editor.current = { editing: Boolean(st?.editing), busy: Boolean(st?.busy) };
    setEditorState(editor.current);
  }, []);
  useEffect(() => { editor.current = IDLE; setEditorState(IDLE); pending.current = null; setAsk(null); }, [projectId, open]);

  const status = useCallback(() => (active ? { dirty: editor.current.editing || editor.current.busy, busy: editor.current.busy } : { dirty: false, busy: false }), [active]);
  useEffect(() => {
    if (!guardRef) return undefined;
    guardRef.current = status;
    return () => { guardRef.current = null; };
  }, [guardRef, status]);

  // In-app navigation blocker (anchor clicks + back/forward) while dirty.
  useEffect(() => { syncNavigationIndex(); }, [location?.key]);
  useEffect(() => {
    if (!active) return undefined;
    return registerNavigationGuard({
      isBlocking: () => status().dirty,
      onBlocked: (proceed) => askOnce({ proceed, label: "Discard and leave" }),
    });
  }, [active, status]);

  const panel = () => bodyRef.current?.closest(PANEL_SEL) || null;

  // The SlideSheet panel is the dialog so its Close button sits inside it.
  useEffect(() => {
    if (!active) return undefined;
    returnFocus.current = document.activeElement;
    const t = setTimeout(() => {
      const el = panel();
      if (el) { el.setAttribute("role", "dialog"); el.setAttribute("aria-modal", "true"); el.setAttribute("aria-labelledby", "record-drawer-title"); }
      bodyRef.current?.querySelector("[data-testid='record-drawer-title']")?.focus();
    }, 0);
    const onResize = () => setWidth(Math.min(680, window.innerWidth));
    onResize();
    window.addEventListener("resize", onResize);
    const onBefore = (e) => { if (status().dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", onBefore);
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("beforeunload", onBefore);
      const el = returnFocus.current;
      setTimeout(() => { if (el && el.isConnected && typeof el.focus === "function") el.focus(); }, 0);
    };
  }, [active]); // eslint-disable-line react-hooks/exhaustive-deps

  // Resolve against the latest caller-owned sequence, including after a
  // confirmation has waited through a filter, refresh, or ordering change.
  const switchRecord = (target, from) => {
    const nav = navigation.current;
    if (!nav.active || nav.disabled || editor.current.busy || nav.projectId !== from ||
        !nav.ids.includes(from) || !nav.ids.includes(target) || target === from || !nav.onOpenProject) return;
    clearAsk();
    editor.current = IDLE;
    nav.onOpenProject(target);
    bodyRef.current?.querySelector("[data-testid='record-drawer-title']")?.focus();
  };
  const requestRecord = (target) => {
    if (pending.current || editor.current.busy || stackedDialogOpen(panel())) return;
    const nav = navigation.current;
    if (!nav.ids.includes(nav.projectId) || !nav.ids.includes(target) || !nav.onOpenProject) return;
    if (editor.current.editing) askOnce({ target, from: nav.projectId, label: "Discard and switch" });
    else switchRecord(target, nav.projectId);
  };

  const forceClose = () => {
    if (editor.current.busy) return; // never abandon an in-flight mutation
    clearAsk(); editor.current = IDLE; onClose();
  };
  const requestClose = () => {
    if (stackedDialogOpen(panel())) return; // stacked dialog owns Escape/scrim
    const d = drawerCloseDecision(editor.current);
    if (d === "close") forceClose();
    else askOnce({ label: "Discard and close" });
  };
  const confirmDiscard = () => {
    if (editor.current.busy) return;
    const intent = pending.current;
    if (intent?.target) {
      switchRecord(intent.target, intent.from);
      clearAsk();
    } else if (intent?.proceed) { clearAsk(); editor.current = IDLE; intent.proceed(); } else forceClose();
  };

  // Focus containment, including from the programmatically focused title.
  useEffect(() => {
    if (!active) return undefined;
    const onKey = (e) => {
      if (e.key !== "Tab") return;
      const el = panel();
      if (!el || stackedDialogOpen(el)) return;
      const items = [...el.querySelectorAll(FOCUSABLE)].filter((n) => !n.closest("[hidden]"));
      if (!items.length) { e.preventDefault(); return; }
      const first = items[0]; const last = items[items.length - 1];
      const cur = document.activeElement;
      const inside = items.includes(cur);
      if (!inside) { e.preventDefault(); (e.shiftKey ? last : first).focus(); }
      else if (e.shiftKey && cur === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && cur === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [active]);

  const busy = editorState.busy;

  return createPortal(
    <SlideSheet open={active} onClose={requestClose} width={width} layer={LAYERS.stickyChrome + 1} panelTestId="project-record-drawer" scrimTestId="project-record-drawer-scrim" closeTestId="project-record-drawer-close">
      <style>{`
        [data-testid="project-record-drawer"] .wk-av { position: static; display: inline-flex; border-radius: 50%; }
        [data-testid="project-record-drawer"] .wk-avtip { position: absolute; bottom: 100%; left: 0; right: 0; box-sizing: border-box; background: ${T.dark}; color: ${T.white}; font-size: ${T.fsXs}px; padding: 5px 9px; border-radius: 7px; white-space: normal; overflow-wrap: anywhere; opacity: 0; pointer-events: none; z-index: 10; }
        [data-testid="project-record-drawer"] .wk-av:hover .wk-avtip,
        [data-testid="project-record-drawer"] .wk-av:focus-visible .wk-avtip { opacity: 1; }
        [data-testid="project-record-drawer"] [data-testid="participation-strip"] > div { flex-wrap: wrap; }
        @media (max-width: 480px) {
          [data-testid="record-drawer-body"] { padding-left: 14px !important; padding-right: 14px !important; }
          [data-testid="record-drawer-body"] > header { flex-wrap: wrap; gap: 0 !important; }
          [data-testid="project-schedule"] > div:first-child { flex-wrap: wrap; }
        }
      `}</style>
      <div ref={bodyRef} data-testid="record-drawer-body" style={{ padding: "18px 22px 40px", boxSizing: "border-box", minHeight: "100%", background: T.tint || T.lightGray }}>
        <header style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, paddingRight: 36, marginBottom: 10 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: T.fsXxs, fontWeight: T.fwBold, letterSpacing: ".06em", textTransform: "uppercase", color: T.mid }}>Project record</div>
            <h2 id="record-drawer-title" tabIndex={-1} data-testid="record-drawer-title" style={{ margin: "2px 0 0", fontSize: T.fsLg, fontWeight: T.fwHubTitle, color: T.projectsHubNavy, outline: "none", overflowWrap: "anywhere" }}>{projectName || "Project"}</h2>
          </div>
          <PrevNextPager ids={orderedProjectIds} currentId={projectId} onNavigate={requestRecord}
            keyboardActive={active} disabled={navigationDisabled || !onOpenProject || Boolean(ask)} busy={busy} compact showUnavailable
            previousLabel="Previous project" nextLabel="Next project" />
        </header>

        {ask && (
          <div role="alert" data-testid="record-drawer-unsaved" style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", padding: "8px 12px", borderRadius: 8, background: T.utilAmber, fontSize: T.fsSm, marginBottom: 10 }}>
            <span data-testid={busy ? "record-drawer-wait" : "record-drawer-unsaved-text"} style={{ flex: 1, minWidth: 160 }}>
              {busy ? "A record change is still saving. Wait for it to finish before leaving." : "The record has an open editor. Leaving now discards unsaved changes."}
            </span>
            <button type="button" data-testid="record-drawer-keep" onClick={clearAsk} style={btn}>Keep editing</button>
            <button type="button" data-testid="record-drawer-discard" disabled={busy} onClick={confirmDiscard} style={{ ...btn, opacity: busy ? 0.5 : 1, cursor: busy ? "not-allowed" : "pointer" }}>{ask.label}</button>
          </div>
        )}

        <p data-testid="record-drawer-provenance" style={{ margin: "0 0 10px", fontSize: T.fsXs, color: T.mid }}>{recordProvenance({ frozen, readOnly, origin })}</p>

        <section aria-label="Ownership and tracks" data-testid="record-drawer-participation" style={{ position: "relative", background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, padding: "2px 16px", marginBottom: 14 }}>
          <Participation participation={participation} frozen={frozen} />
        </section>

        {projectId && (
          <ProjectFullPage key={projectId} projectId={projectId} currentRole={currentRole} readOnly={readOnly || frozen} recordOnly
            onArchived={(id) => { clearAsk(); editor.current = IDLE; onArchived?.(id); onClose(); }}
            onRecordSaved={onRecordSaved} onEditorStateChange={onEditorStateChange} recordTabResetToken={openToken} />
        )}
      </div>
    </SlideSheet>, document.body
  );
}

export default ProjectRecordDrawer;
